/**
 * Notification Context
 *
 * Global state management for notifications:
 * - Subscribe to notification_delivery_log via Supabase Realtime
 * - Show toast when new notification arrives (when app is open)
 * - Track unread count
 * - Provide mark as read functions
 * - Provide notification history
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSupabaseAuth } from './SupabaseAuthContext';
import { supabase } from '../lib/supabaseClient';

const NotificationContext = createContext();

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}

export function NotificationProvider({ children }) {
  const { user } = useSupabaseAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [toastNotification, setToastNotification] = useState(null);

  // Fetch notification history
  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/notifications/history?limit=50`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const data = await response.json();

      if (data.success) {
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }

      setLoading(false);

    } catch (error) {
      console.error('[Notifications] Fetch error:', error);
      setLoading(false);
    }
  }, [user]);

  // Subscribe to new notifications via Supabase Realtime
  useEffect(() => {
    if (!user) return;

    // Initial fetch
    fetchNotifications();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('notification_delivery_log')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notification_delivery_log',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('[Notifications] New notification received:', payload);

          const newNotification = payload.new;

          // Add to notifications list
          setNotifications((prev) => [newNotification, ...prev]);

          // Increment unread count
          setUnreadCount((prev) => prev + 1);

          // Show toast (in-app notification)
          setToastNotification(newNotification);

          // Auto-dismiss toast after 5 seconds
          setTimeout(() => {
            setToastNotification(null);
          }, 5000);
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
    };

  }, [user, fetchNotifications]);

  // Mark single notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) return;

      const response = await fetch(`${API_URL}/notifications/${notificationId}/mark-read`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to mark notification as read');
      }

      // Update local state
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notificationId
            ? { ...notif, read_at: new Date().toISOString() }
            : notif
        )
      );

      // Decrement unread count
      setUnreadCount((prev) => Math.max(0, prev - 1));

    } catch (error) {
      console.error('[Notifications] Mark as read error:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) return;

      const response = await fetch(`${API_URL}/notifications/mark-all-read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to mark all as read');
      }

      // Update local state
      const now = new Date().toISOString();
      setNotifications((prev) =>
        prev.map((notif) => ({ ...notif, read_at: notif.read_at || now }))
      );

      setUnreadCount(0);

    } catch (error) {
      console.error('[Notifications] Mark all as read error:', error);
    }
  }, []);

  // Dismiss toast notification
  const dismissToast = useCallback(() => {
    setToastNotification(null);
  }, []);

  // Refresh notifications
  const refresh = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const value = {
    notifications,
    unreadCount,
    loading,
    toastNotification,
    markAsRead,
    markAllAsRead,
    dismissToast,
    refresh
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}
